/**
 * AI Center(V2) 대화방 API (2026-10-07, aibot-v2-conversation-rooms).
 *
 *   GET    /api/chat/v2/conversations?state=active|hidden       (전부 한 번에 · 제목 검색은 화면에서)
 *   GET    /api/chat/v2/conversations/{idConv}
 *   GET    /api/chat/v2/conversations/{idConv}/turns?beforeSeq=&limit=
 *   PATCH  /api/chat/v2/conversations/{idConv}        {title}
 *   POST   /api/chat/v2/conversations/{idConv}/hide
 *   POST   /api/chat/v2/conversations/{idConv}/restore
 *
 * 회사 · 사용자는 세션에서 서버가 꺼낸다. 여기서 보내지 않는다.
 * 옛 턴은 읽기 전용이다 — 화면 조작 · 호출 id 는 오지 않고, 와도 실행하지 않는다.
 * 별칭(@shared) 없이 상대 경로만 쓴다 — 테스트가 별칭 없이 돈다.
 */
import { apiFetch } from "./api";
import type { ChatAttachmentRefV2, ChatChartPayload, ChatTablePayload } from "./chatApi";

export type ChatRoomState = "ACTIVE" | "HIDDEN";

export interface ChatRoom {
  idConv: string;
  title: string | null;
  cdMenu: string | null;
  noIssue: string | null;
  qtTurn: number;
  tmReg?: string | null;
  tmLast?: string | null;
  tmHide?: string | null;
  state: ChatRoomState;
}

export interface ChatRoomTurn {
  noSeq: number;
  question: string | null;
  answer: string | null;
  tpBasis?: string | null;
  tmScreen?: string | null;
  tmTurn?: string | null;
  error: boolean;
  chart?: ChatChartPayload | null;
  table?: ChatTablePayload | null;
  /** 이 기능 전 턴 — 차트 · 표가 있었는지 알 수 없다 */
  legacyNoSnapshot: boolean;
  /** 이 턴의 질문이 답장한 앞 답 (2026-10-15). 없으면 null */
  quote?: string | null;
  /** 이 답에 쓴 포인트 (2026-10-21). 없으면 null */
  qtPoint?: number | null;
  /** 답한 역할 (2026-10-22). 없으면 null — 일반 답 */
  cdRole?: string | null;
  /** 말풍선 태그 번호 — 질문 · 답 (2026-10-22) */
  tagsQ?: number[];
  tagsA?: number[];
  /** 이 턴의 첨부 칩 메타 (2026-10-26). 바이트 없음 */
  attachments?: ChatRoomAttachmentMeta[] | null;
  /** 저장된 질문 중 입력한 글의 길이 — 첨부가 있던 턴만 (재리뷰 2026-10-26) */
  typedChars?: number | null;
}

/** 첨부 칩 메타 — kind TEXT · IMAGE · DOCUMENT · TABLE, 글자 수(TEXT) · 바이트(그림 · 파일) */
export interface ChatRoomAttachmentMeta {
  kind: string;
  name: string;
  chars?: number | null;
  size?: number | null;
  /** TEXT — 저장된 질문 안의 본문 위치 · 길이(UTF-16, JS 문자열 인덱스와 같다). 서버가 저장 시점에 남긴다 */
  pos?: number | null;
  len?: number | null;
  /** 그림 · 파일 — 첨부 번호. 다시 연 방에서 그림을 받아 오고 다시 시도에 싣는다 (2026-10-27) */
  id?: string | null;
}

export interface ChatRoomTurns {
  items: ChatRoomTurn[];
  hasMore: boolean;
  oldestSeq: number;
  hasLegacy: boolean;
}

const BASE = "/api/chat/v2/conversations";

function query(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** 본인 방 전부. 제목 검색은 받은 목록에서 filterRoomsByTitle 로 거른다 (서버에 글자마다 묻지 않는다) */
export async function listChatRooms(state: "active" | "hidden" = "active"): Promise<ChatRoom[]> {
  const res = await apiFetch<ChatRoom[]>(`${BASE}${query({ state })}`);
  return Array.isArray(res.data) ? res.data : [];
}

/** 제목에 검색어가 들어간 방만. 대소문자 · 앞뒤 공백 무시, 비면 전부 */
export function filterRoomsByTitle(rooms: ChatRoom[], q: string): ChatRoom[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return rooms;
  return rooms.filter((r) => (r.title || "").toLowerCase().includes(needle));
}

export async function fetchChatRoom(idConv: string): Promise<ChatRoom | null> {
  const res = await apiFetch<ChatRoom>(`${BASE}/${encodeURIComponent(idConv)}`);
  return res.data ?? null;
}

export async function fetchChatRoomTurns(
  idConv: string,
  opts: { beforeSeq?: number; limit?: number } = {}
): Promise<ChatRoomTurns> {
  const res = await apiFetch<ChatRoomTurns>(
    `${BASE}/${encodeURIComponent(idConv)}/turns${query({ beforeSeq: opts.beforeSeq, limit: opts.limit })}`
  );
  return res.data ?? { items: [], hasMore: false, oldestSeq: 0, hasLegacy: false };
}

/** 방 안 내용 검색 (2026-10-22) — 검색어가 든 턴 번호(오래된 것부터). 화면이 옛 턴을 어디까지 불러올지 정한다 */
export async function searchChatRoom(idConv: string, q: string): Promise<number[]> {
  const res = await apiFetch<number[]>(`${BASE}/${encodeURIComponent(idConv)}/search${query({ q })}`);
  return Array.isArray(res.data) ? res.data : [];
}

/* ------------------------------------------------------------------ 말풍선 태그 (2026-10-22) */

/** 내 태그 — 이름 ↔ 아이콘(cdIcon 은 chatTags.tsx 의 TAG_ICONS 코드) */
export interface ChatTag {
  noTag: number;
  cdIcon: string;
  nmTag: string;
  noOrder?: number | null;
}

/** 모든 방 모아 보기 한 줄 */
export interface ChatTagHit {
  idConv: string;
  noSeq: number;
  /** true 답 말풍선 · false 질문 말풍선 */
  answer: boolean;
  title: string | null;
  snippet: string;
  tmTurn: string | null;
}

const TAG_BASE = "/api/chat/v2/tags";

export async function fetchChatTags(): Promise<ChatTag[]> {
  const res = await apiFetch<ChatTag[]>(TAG_BASE);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createChatTag(cdIcon: string, nmTag: string): Promise<ChatTag | null> {
  const res = await apiFetch<ChatTag>(TAG_BASE, { method: "POST", body: { cdIcon, nmTag } as unknown as BodyInit });
  return res.data ?? null;
}

export async function updateChatTag(noTag: number, cdIcon: string, nmTag: string): Promise<ChatTag | null> {
  const res = await apiFetch<ChatTag>(`${TAG_BASE}/${noTag}`, {
    method: "PATCH",
    body: { cdIcon, nmTag } as unknown as BodyInit,
  });
  return res.data ?? null;
}

export async function hideChatTag(noTag: number): Promise<void> {
  await apiFetch<boolean>(`${TAG_BASE}/${noTag}/hide`, { method: "POST" });
}

/** 모든 방에서 그 태그가 달린 말풍선 — 최근 것부터 */
export async function fetchTagHits(noTag: number): Promise<ChatTagHit[]> {
  const res = await apiFetch<ChatTagHit[]>(`${TAG_BASE}/${noTag}/hits`);
  return Array.isArray(res.data) ? res.data : [];
}

/** 말풍선에 달기 · 떼기 — 이미 달려 있으면 뗀다. 그 말풍선의 태그 번호를 돌려받는다 */
export async function toggleBubbleTag(idConv: string, seq: number, answer: boolean, noTag: number): Promise<number[]> {
  const res = await apiFetch<number[]>(`${BASE}/${encodeURIComponent(idConv)}/turns/${seq}/tags`, {
    method: "POST",
    body: { answer, noTag } as unknown as BodyInit,
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** 방 안에서 그 태그가 달린 턴 번호 — 화면이 거기까지 불러온다 */
export async function searchChatRoomByTag(idConv: string, noTag: number): Promise<number[]> {
  const res = await apiFetch<number[]>(`${BASE}/${encodeURIComponent(idConv)}/search${query({ tag: noTag })}`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function renameChatRoom(idConv: string, title: string): Promise<ChatRoom | null> {
  const res = await apiFetch<ChatRoom>(`${BASE}/${encodeURIComponent(idConv)}`, {
    method: "PATCH",
    body: { title } as unknown as BodyInit,
  });
  return res.data ?? null;
}

export async function hideChatRoom(idConv: string): Promise<ChatRoom | null> {
  const res = await apiFetch<ChatRoom>(`${BASE}/${encodeURIComponent(idConv)}/hide`, { method: "POST" });
  return res.data ?? null;
}

export async function restoreChatRoom(idConv: string): Promise<ChatRoom | null> {
  const res = await apiFetch<ChatRoom>(`${BASE}/${encodeURIComponent(idConv)}/restore`, { method: "POST" });
  return res.data ?? null;
}

/* ------------------------------------------------------------------ 순수 변환 */

/** 복원한 턴 한 줄 → 말풍선 재료. 화면 조작 · 호출 id · 평가 버튼은 없다 */
export interface ChatRoomHistoryItem {
  role: "user" | "assistant";
  text: string;
  chart?: ChatChartPayload;
  table?: ChatTablePayload;
  error?: boolean;
  /** 이 턴은 스냅샷 전 턴 — 글만 있다 */
  legacy?: boolean;
  /** 질문이 답장한 앞 답 — 내 말풍선 위에 보인다 */
  quote?: string;
  /** 턴 시각(서버 tm_turn) — 질문 · 답이 같은 시각이다. 날짜 구분선 · 시간에 쓴다 */
  at?: string;
  /** 답에 쓴 포인트 — 시간에 마우스를 올리면 보인다 */
  point?: number;
  /** 답한 역할 — 말풍선 위 배지 */
  answerRole?: string;
  /** 턴 번호 · 말풍선 태그 (2026-10-22) */
  turnSeq?: number;
  tags?: number[];
  /** 질문에 붙었던 첨부 칩 메타 (2026-10-26) — 질문 말풍선에 칩으로 보인다 */
  attachments?: ChatRoomAttachmentMeta[];
  /** 복원한 긴 글 칩(그림 없는 턴만) — "다시 시도" 가 그대로 다시 보낸다 (리뷰 F1) */
  sentAttachments?: ChatAttachmentRefV2[];
}

/**
 * 저장된 질문(dc_command)은 "입력한 글 + [붙여넣은 내용: 이름] 블록 + [첨부: 이미지 N장(…)] 한 줄" 을 합친 글이다(BE AiAttachmentCollector).
 * 첨부 메타가 있을 때만 그 표식에서 잘라 입력한 글만 남긴다 — 긴 글은 칩으로 접힌다(aibot-v2-attachments 결정 3)
 */
export function stripAttachmentBlocks(
  question: string,
  attachments?: ChatRoomAttachmentMeta[] | null,
  typedChars?: number | null
): string {
  if (!attachments || attachments.length === 0) return question;
  const parts = splitStoredQuestion(question, attachments, typedChars);
  if (parts.ok) return parts.typed.trim();
  /* 메타와 글이 안 맞는 행 — 앞에서 찾는 옛 방식으로 보이기만 자른다 */
  let text = question;
  let cut = false;
  const textMark = text.indexOf("\n\n[붙여넣은 내용: ");
  if (textMark >= 0 && attachments.some((a) => a.kind === "TEXT")) {
    text = text.slice(0, textMark);
    cut = true;
  }
  const imageMark = text.lastIndexOf("\n\n[첨부: 이미지 ");
  if (imageMark >= 0 && attachments.some((a) => a.kind === "IMAGE")) {
    text = text.slice(0, imageMark);
    cut = true;
  }
  /* 잘라서 비면 그대로 빈 글 — 글 없이 칩만 보낸 턴이다. 원문으로 되돌리면 긴 글 전문이 말풍선에 드러난다 (리뷰 F6) */
  return cut ? text.trim() : question;
}

type StoredQuestionParts = { typed: string; texts: { name: string; text: string }[]; ok: boolean };

/**
 * 저장된 합친 글 = 입력 글 + ("\n\n[붙여넣은 내용: 이름]\n" + 본문)… + ("\n\n[첨부: 이미지 N장(…)]")?
 * 본문 안에도 같은 모양의 글이 있을 수 있어 앞에서부터 찾지 않는다 — 메타에 칩 이름과 순서가 있으니
 * 뒤에서부터 "그 이름의 머리" 를 하나씩 되짚는다. 머리를 못 찾으면 ok=false — 되살리지 않는다 (재리뷰 3, 2026-10-26)
 */
function splitStoredQuestion(
  question: string,
  attachments: ChatRoomAttachmentMeta[],
  typedChars?: number | null
): StoredQuestionParts {
  /* 서버가 저장 시점에 남긴 위치 · 길이가 있으면 그대로 — 글 안에 같은 표식이 있어도 틀리지 않는다 (재리뷰 2026-10-26) */
  const textMetas = attachments.filter((a) => a.kind === "TEXT");
  if (textMetas.every((a) => typeof a.pos === "number" && typeof a.len === "number")) {
    const texts = textMetas.map((a) => ({
      name: a.name,
      text: question.slice(a.pos as number, (a.pos as number) + (a.len as number)),
    }));
    let typedEnd: number;
    if (typeof typedChars === "number") typedEnd = typedChars;
    else if (textMetas.length > 0)
      typedEnd = Math.max(0, (textMetas[0].pos as number) - `\n\n[붙여넣은 내용: ${textMetas[0].name}]\n`.length);
    else {
      const m = question.lastIndexOf("\n\n[첨부: 이미지 ");
      typedEnd = m >= 0 ? m : question.length;
    }
    return { typed: question.slice(0, typedEnd), texts, ok: true };
  }
  /* 위치가 없는 옛 행 — 뒤에서부터 머리를 되짚는다 */
  let text = question;
  if (attachments.some((a) => a.kind === "IMAGE")) {
    const imageMark = text.lastIndexOf("\n\n[첨부: 이미지 ");
    if (imageMark >= 0) text = text.slice(0, imageMark);
  }
  const names = attachments.filter((a) => a.kind === "TEXT").map((a) => a.name);
  const texts: { name: string; text: string }[] = [];
  let end = text.length;
  for (let i = names.length - 1; i >= 0; i--) {
    const header = `\n\n[붙여넣은 내용: ${names[i]}]\n`;
    const idx = text.lastIndexOf(header, Math.max(0, end - header.length));
    if (idx < 0 || idx + header.length > end) return { typed: text, texts: [], ok: false };
    texts.unshift({ name: names[i], text: text.slice(idx + header.length, end) });
    end = idx;
  }
  return { typed: text.slice(0, end), texts, ok: true };
}

/**
 * 저장된 턴의 첨부를 "다시 시도" 참조 모양으로 되살린다 — 긴 글은 저장된 합친 글에서(마스킹된 글), 그림은 저장소 번호로.
 * 번호가 없는 그림(첨부 저장 전에 보낸 옛 턴)이 섞이면 되살리지 않는다 — 그대로 다시 보낼 수 없다
 */
export function restoreSentAttachments(
  question: string,
  attachments?: ChatRoomAttachmentMeta[] | null,
  typedChars?: number | null
): ChatAttachmentRefV2[] {
  if (!attachments || attachments.length === 0) return [];
  if (attachments.some((a) => a.kind !== "TEXT" && !((a.kind === "IMAGE" || a.kind === "DOCUMENT" || a.kind === "TABLE") && a.id))) return [];
  const hasText = attachments.some((a) => a.kind === "TEXT");
  const parts = hasText ? splitStoredQuestion(question, attachments, typedChars) : { ok: true, texts: [], typed: "" };
  if (!parts.ok) return [];
  const texts = [...parts.texts];
  const out: ChatAttachmentRefV2[] = [];
  for (const a of attachments) {
    if (a.kind === "TEXT") {
      const t = texts.shift();
      if (t && t.text.trim()) out.push({ kind: "TEXT", name: t.name, text: t.text });
    } else {
      out.push({ id: a.id as string, kind: a.kind === "DOCUMENT" || a.kind === "TABLE" ? a.kind : "IMAGE", name: a.name });
    }
  }
  return out;
}

/** 질문 → 답 순서로 편다. 화면 기준 답에는 조회시각을 붙여 "낡은 답" 임을 알 수 있게 한다 */
export function turnsToHistory(turns: ChatRoomTurn[]): ChatRoomHistoryItem[] {
  const out: ChatRoomHistoryItem[] = [];
  for (const t of turns) {
    if (t.question && t.question.trim()) {
      const sentAttachments = restoreSentAttachments(t.question, t.attachments, t.typedChars);
      out.push({
        role: "user",
        text: stripAttachmentBlocks(t.question, t.attachments, t.typedChars),
        ...(t.attachments && t.attachments.length > 0 ? { attachments: t.attachments } : {}),
        ...(sentAttachments.length > 0 ? { sentAttachments } : {}),
        ...(t.quote ? { quote: t.quote } : {}),
        ...(t.tmTurn ? { at: t.tmTurn } : {}),
        turnSeq: t.noSeq,
        ...(t.tagsQ && t.tagsQ.length > 0 ? { tags: t.tagsQ } : {}),
      });
    }
    const answer = t.answer && t.answer.trim() ? t.answer : "(답변이 비어 있습니다)";
    /* 라이브 답(chatApi.withBasis)과 같은 꼬리말 모양 — 복원했을 때 달라 보이면 안 된다 */
    const basis =
      t.tpBasis === "SCREEN" && t.tmScreen ? `\n\n_화면 기준 (${formatTurnTime(t.tmScreen)} 조회)_` : "";
    out.push({
      role: "assistant",
      text: answer + basis,
      ...(t.chart && Array.isArray(t.chart.data) ? { chart: t.chart } : {}),
      ...(t.table && Array.isArray(t.table.rows) ? { table: t.table } : {}),
      ...(t.error ? { error: true } : {}),
      ...(t.legacyNoSnapshot ? { legacy: true } : {}),
      ...(t.tmTurn ? { at: t.tmTurn } : {}),
      ...(typeof t.qtPoint === "number" ? { point: t.qtPoint } : {}),
      ...(t.cdRole ? { answerRole: t.cdRole } : {}),
      turnSeq: t.noSeq,
      ...(t.tagsA && t.tagsA.length > 0 ? { tags: t.tagsA } : {}),
    });
  }
  return out;
}

/** "MM-DD HH:mm". 파싱이 안 되면 원문 */
export function formatTurnTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 대화 목록 시간 — 오늘이면 "오후 1:37", 어제면 "어제", 올해면 "10-19", 그 전이면 "2025-10-19" (카톡처럼, 2026-10-21) */
export function formatRoomTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const dayOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((dayOf(now) - dayOf(d)) / 86400000);
  if (days === 0) return formatChatTime(value);
  if (days === 1) return "어제";
  const p = (n: number) => String(n).padStart(2, "0");
  const md = `${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return d.getFullYear() === now.getFullYear() ? md : `${d.getFullYear()}-${md}`;
}

/** 말풍선 옆 시간 — "오후 1:25" (카톡처럼). 파싱이 안 되면 빈 문자열 (2026-10-15) */
export function formatChatTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  return `${h < 12 ? "오전" : "오후"} ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** 날짜 구분선 — "2026년 10월 15일 목요일". 파싱이 안 되면 빈 문자열 */
export function formatChatDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const day = ["일", "월", "화", "수", "목", "금", "토"][d.getDay()];
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${day}요일`;
}

/**
 * 말풍선마다 날짜 구분선 · 시간을 붙일지 (카톡 규칙).
 * 구분선 — 시각이 있는 말풍선 중 날짜가 앞과 달라지는 첫 말풍선. 시간 — 같은 분에 같은 쪽이 연달아 보낸 것 중 마지막
 */
export function chatTimeline(
  messages: { role: string; at?: string }[]
): { date: string; time: string }[] {
  let lastDay = "";
  return messages.map((m, i) => {
    const date = formatChatDate(m.at);
    const showDate = date !== "" && date !== lastDay;
    if (date) lastDay = date;
    const time = formatChatTime(m.at);
    const next = messages[i + 1];
    const sameRun =
      next !== undefined &&
      next.role === m.role &&
      formatChatDate(next.at) === date &&
      formatChatTime(next.at) === time;
    return { date: showDate ? date : "", time: sameRun ? "" : time };
  });
}

/** 목록에 보일 제목. 제목이 없으면(비운 방 등) 자리 문구 */
export function roomTitle(room: Pick<ChatRoom, "title">): string {
  const t = room.title && room.title.trim();
  return t || "(제목 없음)";
}

/** 옛 턴 안내 — 방에 스냅샷 전 턴이 하나라도 있을 때 한 번만 */
export const LEGACY_ROOM_NOTICE =
  "이 대화의 일부는 표·차트 보관 기능이 생기기 전 것이라 글만 복원됩니다.";
